import { createSelector, createSlice } from '@reduxjs/toolkit';
import v4 from 'uuid/v4';
import { fetchNerobottiThreads } from './thunks/fetchNerobottiThreads';
import { openNerobottiThread } from './thunks/openNerobottiThread';

/**
 * Nerobotti conversation state.
 *
 * Replaces `scenes/Settings/Chatbot/store/slices/chatbotSlice.js`, which is
 * deprecated along with the rest of the old bot. Three things are deliberately
 * gone:
 *
 *  - `isAdmin` / `isBulletin`. There are no modes any more. The assistant picks
 *    tools based on the question; the user does not pick a data source.
 *  - Everything scope-shaped. The old client sent `businessId`, `projectKey`
 *    and `admin` with every message and the backend believed them, which is how
 *    any authenticated user could read any tenant's documents. The client now
 *    sends the message and nothing that grants anything.
 *  - Silent failure. The old slice had an `evt.type === 'error'` branch that
 *    nothing ever emitted, so a backend failure left `isLoading` true forever
 *    with the send button disabled until reload. Every terminal path here
 *    clears it.
 */

/**
 * Where the window is. One field, not two booleans.
 *
 *  - `closed`    — nothing rendered, nothing connected. The panel is unmounted
 *                  and the socket with it.
 *  - `open`      — the panel is on screen.
 *  - `minimized` — the panel is still mounted and still connected; it is drawn
 *                  as a chip in the corner instead of as a window.
 *
 * The distinction that matters is between `closed` and `minimized`, and it is
 * not cosmetic: `NerobottiPanel` owns the socket, so unmounting it drops the
 * connection and abandons whatever turn was in flight. Minimizing must not do
 * that — waiting out a slow reply with the window out of the way is the whole
 * point of it — so `NerobottiPanelHost` keeps the panel mounted for both `open`
 * and `minimized`, and only `closed` takes it down. That invariant is the
 * feature; everything else here is bookkeeping around it.
 *
 * An enum rather than `isOpen` + `isMinimized` because those two have a fourth
 * combination — closed *and* minimized — that means nothing, and a reachable
 * meaningless state is exactly what stranded the composer: the store could sit
 * in "awaiting a reply" with no panel and no socket left to deliver one.
 */
export const PANEL_CLOSED = 'closed';
export const PANEL_OPEN = 'open';
export const PANEL_MINIMIZED = 'minimized';

const initialState = {
    panelState: PANEL_CLOSED,
    isFullscreen: false,
    /**
     * Something arrived while the panel was minimized and has not been looked
     * at. Drawn as a dot on the chip — a reply that lands behind a docked
     * window is otherwise indistinguishable from one that never came.
     */
    hasUnseenReply: false,
    /**
     * Turns, in order. Two shapes:
     *
     *  - `{ id, role: 'user' | 'assistant', content, status, timestamp }`
     *  - `{ id, role: 'action', actionId, summary, irreversible, expiresAt,
     *     state, outcome, link, timestamp }` — a change the assistant offered,
     *    awaiting a tap. `link` is `{ url, label }` and arrives only once the
     *    change succeeded. `irreversible` comes off the frame, never inferred.
     *
     * The action item is the only interactive thing in the stream, and it is
     * deliberately in the stream rather than in a modal: what is being agreed to
     * belongs next to the sentence that proposed it.
     */
    discussion: [],
    /** Groups turns into one conversation so the assistant can follow context. */
    threadId: null,
    isAwaitingReply: false,
    error: null,
    connectionState: 'idle', // idle | connecting | open | closed
    /**
     * What the user is looking at, published by whichever scene is on screen.
     *
     * `{ owner, view, projectId, bulletinId }`, or null. Sent with each message
     * as an untrusted hint so the assistant knows what "tämä" refers to; the
     * server allowlists the view and re-checks the ids against the caller's own
     * scope, so nothing here grants anything.
     *
     * `owner` is a per-mount token, not data — see `clearViewContext`. It is
     * stripped before the context is sent.
     */
    viewContext: null,

    /**
     * Which of the two the user last asked for: the conversation, or the list
     * of past ones.
     *
     * Deliberately not "which one is on screen", because that is not a thing
     * one flag can say any more. Where there is only room for one — the 420px
     * dock, and a phone, where a rail beside the stream would leave neither
     * readable — this picks it, and a swap is the whole layout. Where there is
     * room for both, `NerobottiPanel` draws the list as a rail and a
     * conversation beside it whatever this says, and this is only what the
     * panel falls back to when the room goes away.
     *
     * Which is why nothing here reacts to `toggleFullscreen`. Every crossing
     * either way lands on the screen the user last chose, and a window resized
     * across the threshold and back comes back exactly where it was.
     */
    view: 'chat', // chat | history

    /**
     * The conversation list. Paged, so `cursor` is where the next page starts
     * and null means there is no next page.
     */
    threads: {
        items: [],
        status: 'idle', // idle | loading | ready | failed
        cursor: null,
        /** True while a `Load more` is in flight, so the list is not blanked. */
        isLoadingMore: false,
        /** A row failed to open. Shown on the list, not on the chat screen. */
        openFailed: false,
    },

    /** The thread being fetched, so its row alone can show it is opening. */
    openingThreadId: null,

    /**
     * Set when the transcript just loaded was too long to return whole. The
     * panel says so rather than silently showing a conversation that appears to
     * begin in the middle.
     */
    isTranscriptTruncated: false,
};

/**
 * Turn stored exchanges into the panel's own turn list.
 *
 * One stored row is one *exchange* — both halves together — while the stream
 * renders a sequence of turns, so an ordinary row becomes two items. Ids are
 * minted fresh: they only ever key the React list, and nothing stored is stable
 * enough to reuse for that.
 *
 * ## A confirmed action is one card, not two messages
 *
 * The server files a confirmation as a turn whose `userMessage` is the summary
 * behind a `[Vahvistettu]` prefix and whose `assistantMessage` is the outcome —
 * a shape chosen for the *model*, which is shown the confirmation as if the
 * user had spoken it. Replaying that literally is what made a reopened thread
 * look wrong: a bubble for a sentence nobody typed, a second bubble for text
 * that belongs on the card, and no card at all.
 *
 * Live, the outcome never becomes a message — `receiveSocketEvent` routes a
 * `done` carrying an `actionId` onto the card instead. So one row with an
 * `action` becomes exactly one `action` item, and the two messages are dropped.
 * That is not hiding them: the card carries both, in the places they belong.
 *
 * ## Rows written before the server recorded the outcome
 *
 * Those get the same card in an `archived` state: the summary and the outcome
 * text are both in the row, only the verdict is missing, so the layout matches
 * live and the rule stays grey rather than claiming a success or a failure.
 *
 * Recognising them does mean matching the Finnish `[Vahvistettu]` prefix — the
 * exact parsing the stored `action` record exists to avoid. It is acceptable
 * *here* and nowhere else: it applies only to rows that can never gain that
 * record, and it asserts nothing the prefix does not already say. It is
 * deliberately not a fallback for new rows, which is why it does not try to
 * infer a status. These age out with the 90-day retention, and this goes with
 * them.
 */
/**
 * The marker the server puts in front of a confirmation's summary when it files
 * it as a turn, because the *model* is shown the confirmation as though the user
 * had spoken it.
 */
const CONFIRMED_PREFIX = '[Vahvistettu] ';

/**
 * The summary of a confirmation stored before the server recorded its outcome,
 * or null for any other turn.
 *
 * Requires the outcome half to be present too. That does not make the match
 * airtight — someone could type the prefix themselves and get their own
 * sentence drawn as a grey card — but the cost of that is cosmetic and confined
 * to their own transcript, which is the right side to be wrong on for a
 * fallback that exists only until these rows expire.
 */
const legacyConfirmSummary = (turn) => {
    const userMessage = turn?.userMessage;

    if (typeof userMessage !== 'string') return null;
    if (!userMessage.startsWith(CONFIRMED_PREFIX)) return null;
    if (!turn?.assistantMessage) return null;

    const summary = userMessage.slice(CONFIRMED_PREFIX.length).trim();

    return summary || null;
};

const discussionFromTurns = (turns) =>
    turns.flatMap((turn) => {
        const parsed = turn?.createdAt ? Date.parse(turn.createdAt) : NaN;
        const timestamp = Number.isNaN(parsed) ? Date.now() : parsed;

        /** Every replayed card is spent, so none of them can be acted on. */
        const settledCard = (summary, state, link) => ({
            id: v4(),
            role: 'action',
            // No actionId: the offer is long spent, and a terminal card renders
            // no buttons, so nothing can be dispatched for it.
            actionId: null,
            summary,
            // Nothing to warn about on a card nobody can act on, and the stored
            // row does not say either way. What it was is history now, and the
            // state colour is the part of it worth drawing.
            irreversible: false,
            state,
            outcome: turn.assistantMessage || null,
            link,
            expiresAt: null,
            timestamp,
        });

        if (turn?.action?.summary) {
            return [
                settledCard(
                    turn.action.summary,
                    turn.action.status === 'ok' ? 'done' : 'failed',
                    turn.action.link ?? null
                ),
            ];
        }

        const legacySummary = legacyConfirmSummary(turn);

        if (legacySummary) {
            return [settledCard(legacySummary, 'archived', null)];
        }

        const items = [];

        if (turn?.userMessage) {
            items.push({
                id: v4(),
                role: 'user',
                content: turn.userMessage,
                status: 'final',
                timestamp,
            });
        }

        if (turn?.assistantMessage) {
            items.push({
                id: v4(),
                role: 'assistant',
                content: turn.assistantMessage,
                status: 'final',
                timestamp,
            });
        }

        return items;
    });

export const nerobottiSlice = createSlice({
    name: 'nerobotti',
    initialState,
    reducers: {
        openNerobotti: (state) => {
            showPanel(state);
        },

        /**
         * Closing takes the panel down, and with it the socket — so this is the
         * moment the in-flight turn dies, and the moment to say so.
         *
         * Nothing else was saying it. The teardown deliberately nulls
         * `onclose` before closing the socket so a panel being dismissed cannot
         * schedule a reconnect, which means `setConnectionState('closed')` —
         * the one path that clears `isAwaitingReply` — never runs. The store
         * was left waiting for a reply that no longer had anything to arrive
         * on, and reopening could not clear it either: the panel comes back at
         * `idle`, and this reducer returns early for every state but `closed`.
         * The composer stayed disabled until the user started a new
         * conversation or opened an old one.
         *
         * That is the bug the assistant this replaces was built to be rid of —
         * a UI stuck waiting forever — arriving through the one door the
         * teardown comments do not cover. It is fixed here rather than in the
         * socket because closing is the fact we actually have: the turn is not
         * unreachable because a connection dropped, it is unreachable because
         * the user dismissed the window it was going to land in.
         */
        closeNerobotti: (state) => {
            closePanel(state);
        },

        /**
         * Out of the way, still listening.
         *
         * Deliberately touches nothing but the lifecycle: the panel stays
         * mounted, so the socket, the thread and any turn mid-flight all
         * continue. That is the difference from `closeNerobotti`, and it is the
         * reason a user waiting on a slow answer now has somewhere to put the
         * window other than away.
         */
        minimizeNerobotti: (state) => {
            if (state.panelState !== PANEL_OPEN) return;

            state.panelState = PANEL_MINIMIZED;
        },

        restoreNerobotti: (state) => {
            showPanel(state);
        },

        /**
         * The launcher button.
         *
         * A minimized panel restores rather than closes — the chip is the user
         * saying "later", and the same button that docked it should be able to
         * bring it back without costing them the conversation.
         */
        toggleNerobotti: (state) => {
            if (state.panelState === PANEL_OPEN) {
                closePanel(state);
                return;
            }

            showPanel(state);
        },

        toggleFullscreen: (state) => {
            state.isFullscreen = !state.isFullscreen;
        },

        /**
         * Clear the conversation. A new threadId means a fresh context.
         *
         * This used to be the only way to lose a conversation and it lost it
         * for good — the turns were on the server the whole time, with no way
         * to ask for them. It is an ordinary action now: what it clears is the
         * screen and the pointer, and the conversation is one tap away in the
         * history view.
         */
        startNewConversation: (state) => {
            state.discussion = [];
            state.threadId = null;
            state.isAwaitingReply = false;
            state.error = null;
            state.isTranscriptTruncated = false;
            // Starting a conversation means being in one.
            state.view = 'chat';
        },

        showHistory: (state) => {
            state.view = 'history';
        },

        showChat: (state) => {
            state.view = 'chat';
        },

        setConnectionState: (state, action) => {
            state.connectionState = action.payload;

            if (action.payload !== 'closed') return;

            // A socket that closed while we were waiting is a dead turn. Say so
            // rather than leaving a placeholder spinning.
            abandonInFlightTurn(
                state,
                'Yhteys katkesi. Yritä uudelleen.',
                'Yhteys katkesi. Tarkista tiedotetaulu ennen kuin yrität uudelleen.'
            );
        },

        /** Optimistic: the user's turn plus the placeholder deltas fill in. */
        sendMessage: (state, action) => {
            const { text, threadId } = action.payload;

            // The caller mints the id and passes it in, rather than this
            // reducer inventing one. Minting it here looks tidier and is a
            // trap: `dispatch` does not re-render synchronously, so the panel
            // that just dispatched still reads `null` and sends the message
            // unthreaded. The server then drops the turn on the floor —
            // `saveTurn` requires a thread id — and the first exchange of every
            // conversation is invisible to the next one.
            if (!state.threadId) {
                state.threadId = threadId ?? `${new Date().toISOString()}#${v4()}`;
            }

            state.error = null;
            state.isAwaitingReply = true;

            state.discussion.push({
                id: v4(),
                role: 'user',
                content: text,
                status: 'final',
                timestamp: Date.now(),
            });

            state.discussion.push({
                id: v4(),
                role: 'assistant',
                content: '',
                status: 'streaming',
                timestamp: Date.now(),
            });
        },

        /** A frame from the socket: delta | done | error | confirm | pong. */
        receiveSocketEvent: (state, action) => {
            const event = action.payload;
            const last = lastAssistantTurn(state);

            // Terminal frames only. A `delta` means the reply is still being
            // written, and a dot that lights up the instant the assistant
            // starts typing says nothing about whether it is worth going back
            // for; the frame that ends the turn does.
            if (
                state.panelState === PANEL_MINIMIZED &&
                ['done', 'confirm', 'error'].includes(event?.type)
            ) {
                state.hasUnseenReply = true;
            }

            switch (event?.type) {
                case 'delta': {
                    // The old slice did `last.content += ...` unguarded, which
                    // throws if a delta arrives with no placeholder (a late
                    // frame from an abandoned turn, for instance).
                    if (last && typeof event.text === 'string') {
                        last.content += event.text;
                    }
                    break;
                }

                case 'done': {
                    // A `done` carrying an actionId is the outcome of a
                    // confirmed action, not the end of a reply. It must not
                    // touch the last assistant turn: no placeholder was created
                    // for a button press, so `last` is a finished message from
                    // earlier and writing to it would overwrite real text.
                    if (event.actionId) {
                        resolveActionTurn(state, event);
                        state.isAwaitingReply = false;
                        break;
                    }

                    if (last) {
                        // Prefer the authoritative full text over accumulated
                        // deltas — a dropped frame would otherwise leave a gap.
                        if (typeof event.text === 'string' && event.text) {
                            last.content = event.text;
                        }
                        last.status = 'final';
                    }
                    state.isAwaitingReply = false;
                    break;
                }

                /**
                 * The assistant is offering to change something. Arrives after
                 * `done`, so the reply bubble is already final and the composer
                 * already unlocked — the card lands underneath a finished
                 * message.
                 */
                case 'confirm': {
                    if (!event.actionId) break;

                    state.discussion.push({
                        id: v4(),
                        role: 'action',
                        actionId: event.actionId,
                        // The server's sentence, not the model's prose. This is
                        // the thing being agreed to.
                        summary: event.summary || '',
                        /**
                         * Whether accepting this can be taken back.
                         *
                         * Read off the frame and never inferred here. The
                         * client could recognise a list of tool names instead,
                         * and that is exactly the wrong place for it: the
                         * registry would gain a write this list had never heard
                         * of and the card would go on looking harmless, with
                         * nothing failing to say so. The server knows which of
                         * its own tools cannot be undone; a client guess would
                         * only ever be a stale copy of that.
                         *
                         * Strict `=== true` for the same reason: a frame that
                         * does not carry the field is one that says nothing
                         * about reversibility, not one that says there is
                         * nothing to warn about. That is what an older server
                         * sends, and drawing its offers as harmless would be a
                         * claim nobody made.
                         */
                        irreversible: event.irreversible === true,
                        expiresAt: event.expiresAt ?? null,
                        state: 'pending',
                        outcome: null,
                        link: null,
                        timestamp: Date.now(),
                    });
                    break;
                }

                case 'error': {
                    state.isAwaitingReply = false;
                    state.error = event.message || 'Jokin meni vikaan.';

                    // Same split as `done`: an error about an action belongs on
                    // its card, not appended to an unrelated message.
                    if (event.actionId) {
                        resolveActionTurn(state, {
                            ...event,
                            text: state.error,
                            status: 'error',
                        });
                        break;
                    }

                    failLastAssistantTurn(state, state.error);
                    break;
                }

                default:
                    break;
            }
        },

        /**
         * The user tapped Vahvista. Optimistic only in appearance — nothing is
         * written until the server says so, and the server re-checks permissions
         * before it does.
         */
        confirmAction: (state, action) => {
            const turn = findActionTurn(state, action.payload?.actionId);
            if (!turn || turn.state !== 'pending') return;

            state.error = null;
            turn.state = 'confirming';
            state.isAwaitingReply = true;
        },

        /** The user dismissed the card. Client-side only; the offer just expires. */
        dismissAction: (state, action) => {
            const turn = findActionTurn(state, action.payload?.actionId);
            if (!turn || turn.state !== 'pending') return;

            turn.state = 'dismissed';
        },

        /** The confirm frame never left the client. */
        confirmFailed: (state, action) => {
            const { actionId, message } = action.payload ?? {};
            const turn = findActionTurn(state, actionId);

            state.isAwaitingReply = false;

            if (!turn) return;

            turn.state = 'failed';
            turn.outcome = message || 'Vahvistuksen lähetys epäonnistui.';
        },

        /** The socket refused the message outright (never reached the server). */
        sendFailed: (state, action) => {
            state.isAwaitingReply = false;
            state.error = action.payload || 'Viestin lähetys epäonnistui.';
            failLastAssistantTurn(state, state.error);
        },

        dismissError: (state) => {
            state.error = null;
        },

        /**
         * A scene saying what it is showing.
         *
         * Last writer wins, deliberately: the panel is global and outlives any
         * one scene, so the context should always describe the screen the user
         * is on now rather than the one they opened the assistant from.
         */
        setViewContext: (state, action) => {
            const { owner, ...context } = action.payload ?? {};
            state.viewContext = { owner, ...context };
        },

        /**
         * A scene unmounting.
         *
         * Only clears its *own* context. Without the owner check a scene tearing
         * down after its replacement has already mounted would wipe the new
         * scene's context, leaving the assistant blind on a screen that had
         * just described itself — a bug that would show up as "the hint works
         * except when you navigate between two views that both publish one".
         */
        clearViewContext: (state, action) => {
            if (state.viewContext?.owner !== action.payload) return;

            state.viewContext = null;
        },
    },

    extraReducers: (builder) => {
        builder
            .addCase(fetchNerobottiThreads.pending, (state, action) => {
                // A `Load more` must not blank the list under the user's
                // finger, so only a first page takes the loading state.
                if (action.meta.arg?.cursor) {
                    state.threads.isLoadingMore = true;
                    return;
                }

                state.threads.status = 'loading';
            })
            .addCase(fetchNerobottiThreads.fulfilled, (state, action) => {
                const { threads, cursor, append } = action.payload;

                state.threads.items = append
                    ? [...state.threads.items, ...threads]
                    : threads;
                state.threads.cursor = cursor;
                state.threads.status = 'ready';
                state.threads.isLoadingMore = false;
            })
            .addCase(fetchNerobottiThreads.rejected, (state) => {
                state.threads.isLoadingMore = false;
                // Keeping whatever is already listed: a failed *next* page
                // should cost the new rows, not the ones already being read.
                state.threads.status = state.threads.items.length
                    ? 'ready'
                    : 'failed';
            })

            .addCase(openNerobottiThread.pending, (state, action) => {
                state.openingThreadId = action.meta.arg;
                state.threads.openFailed = false;
            })
            .addCase(openNerobottiThread.fulfilled, (state, action) => {
                const { threadId, turns, truncated } = action.payload;

                state.discussion = discussionFromTurns(turns);
                // Adopting the id is what lets the conversation continue rather
                // than merely be read — see the thunk.
                state.threadId = threadId;
                state.isTranscriptTruncated = truncated;
                state.openingThreadId = null;
                // Not "close the list" — beside a rail the list stays up and
                // this is unseen. It is the same sentence `view` always said:
                // the conversation is what was asked for last. That is what
                // makes leaving fullscreen land on the conversation the user
                // picked in the rail rather than back on the list they picked
                // it from.
                state.view = 'chat';
                // A loaded conversation has nothing in flight by construction.
                state.isAwaitingReply = false;
            })
            .addCase(openNerobottiThread.rejected, (state) => {
                state.openingThreadId = null;
                // Kept out of `state.error`, which holds text the panel prints
                // as-is. This failure belongs to the list the user is still
                // looking at, and the list translates it there.
                state.threads.openFailed = true;
            });
    },
});

/** Bring the window up, from closed or from the chip. */
const showPanel = (state) => {
    state.panelState = PANEL_OPEN;
    state.hasUnseenReply = false;
};

/**
 * Take the window down, and everything that was only going to arrive in it.
 *
 * See `closeNerobotti` for why the abandonment belongs here and not in the
 * socket's teardown.
 */
const closePanel = (state) => {
    state.panelState = PANEL_CLOSED;
    state.hasUnseenReply = false;
    // No socket any more, and none wanted. Leaving 'open' here would have the
    // reopened panel believe it was already connected.
    state.connectionState = 'idle';

    abandonInFlightTurn(
        state,
        'Keskustelu suljettiin ennen vastausta.',
        'Ikkuna suljettiin kesken vahvistuksen. Tarkista lopputulos ennen kuin yrität uudelleen.'
    );
};

/**
 * Everything that was waiting on a socket we no longer have.
 *
 * Two callers, two reasons the socket is gone — it dropped, or the user closed
 * the panel it belonged to — and the same two victims either way: the streaming
 * placeholder, and any card the user had already authorised.
 *
 * A card still in 'pending' is deliberately left alone. The offer lives
 * server-side keyed by user, not by connection, so it survives a reconnect and
 * stays tappable — and it survives the panel being closed and reopened too.
 */
const abandonInFlightTurn = (state, replyMessage, confirmMessage) => {
    if (state.isAwaitingReply) {
        state.isAwaitingReply = false;
        failLastAssistantTurn(state, replyMessage);
    }

    // A card mid-confirm is the same problem with worse consequences: the write
    // may or may not have happened, and there is no longer a socket to be told
    // which. Say exactly that rather than implying either.
    state.discussion.forEach((turn) => {
        if (turn.role === 'action' && turn.state === 'confirming') {
            turn.state = 'failed';
            turn.outcome = confirmMessage;
        }
    });
};

/**
 * The streaming placeholder, if there is one.
 *
 * Note this looks only at the *last* item, so a card sitting at the end of the
 * discussion correctly yields null — a stray delta after an offer has nowhere to
 * go rather than landing on the wrong turn.
 */
const lastAssistantTurn = (state) => {
    const last = state.discussion[state.discussion.length - 1];
    return last && last.role === 'assistant' ? last : null;
};

const findActionTurn = (state, actionId) =>
    actionId
        ? state.discussion.find(
              (turn) => turn.role === 'action' && turn.actionId === actionId
          )
        : undefined;

/**
 * Record what became of a confirmed action.
 *
 * Keyed by actionId rather than by position: the outcome arrives on its own
 * invocation and the user may have sent other messages in between, so the card
 * is not necessarily the last thing in the stream.
 */
const resolveActionTurn = (state, event) => {
    const turn = findActionTurn(state, event.actionId);
    if (!turn) return;

    // Never downgrade a card that already succeeded. A second tap is refused by
    // the server with "already done", which is a true statement but arrives
    // without an ok status — treating it as a failure would turn a bulletin that
    // was genuinely posted red.
    if (turn.state === 'done') return;

    turn.state = event.status === 'ok' ? 'done' : 'failed';
    turn.outcome = event.text || null;
    // Built by the server from ids it already holds — never parsed out of the
    // assistant's prose, for the same reason `summary` isn't.
    turn.link = event.link ?? null;
};

/**
 * Turn a stuck placeholder into a visible failure. Without this the user sees
 * an empty bubble and a spinner that never resolves — the single worst
 * behaviour of the bot this replaces.
 */
const failLastAssistantTurn = (state, message) => {
    const last = lastAssistantTurn(state);
    if (!last || last.status === 'final') return;

    last.status = 'error';
    last.content = last.content
        ? `${last.content}\n\n_${message}_`
        : `_${message}_`;
};

export const {
    openNerobotti,
    closeNerobotti,
    minimizeNerobotti,
    restoreNerobotti,
    toggleNerobotti,
    toggleFullscreen,
    startNewConversation,
    setConnectionState,
    sendMessage,
    receiveSocketEvent,
    sendFailed,
    dismissError,
    confirmAction,
    dismissAction,
    confirmFailed,
    setViewContext,
    clearViewContext,
    showHistory,
    showChat,
} = nerobottiSlice.actions;

// SELECTORS
export const selectNerobotti = (state) => state.nerobottiSlice ?? initialState;
export const selectNerobottiPanelState = (state) =>
    selectNerobotti(state).panelState;

/**
 * On screen. Not the same as mounted — a minimized panel is still mounted and
 * still connected, which is why the host has its own check and this one is only
 * good for the launcher's `aria-expanded`.
 */
export const selectIsNerobottiOpen = (state) =>
    selectNerobottiPanelState(state) === PANEL_OPEN;

export const selectHasUnseenNerobottiReply = (state) =>
    selectNerobotti(state).hasUnseenReply;
export const selectDiscussion = (state) => selectNerobotti(state).discussion;
export const selectIsAwaitingReply = (state) =>
    selectNerobotti(state).isAwaitingReply;
export const selectThreadId = (state) => selectNerobotti(state).threadId;
export const selectNerobottiError = (state) => selectNerobotti(state).error;

export const selectNerobottiView = (state) => selectNerobotti(state).view;
export const selectNerobottiThreads = (state) => selectNerobotti(state).threads;
export const selectOpeningThreadId = (state) =>
    selectNerobotti(state).openingThreadId;
export const selectIsTranscriptTruncated = (state) =>
    selectNerobotti(state).isTranscriptTruncated;

/**
 * Whether the socket is up. The panel says so when it isn't: a dead socket is
 * the failure a user is most likely to hit here, and until now the state was
 * recorded and shown nowhere.
 */
export const selectConnectionState = (state) =>
    selectNerobotti(state).connectionState;

/**
 * The context as it goes on the wire: the scene's description of itself,
 * without the bookkeeping token.
 *
 * Returns undefined rather than null when there is nothing, so it drops out of
 * the JSON frame instead of being sent as an explicit null.
 *
 * The one memoized selector in this file, and it has to be: it builds a new
 * object, so an unmemoized version would hand `useSelector` a fresh reference
 * on every dispatch and re-render the panel on every action in the app —
 * including each of its own streaming deltas.
 */
export const selectViewContextForSend = createSelector(
    (state) => selectNerobotti(state).viewContext,
    (context) => {
        if (!context) return undefined;

        const { owner, ...rest } = context;

        return rest;
    }
);
